import {
  SERVICE_BASIC_CARE,
  SERVICE_BASIC_MEDICAL_CARE,
  SERVICE_CARE_PROTECTED_AREA,
  SERVICE_CLOSED_AREA,
  SERVICE_COMPANIONSHIP,
  SERVICE_DEMENTIA,
  SERVICE_PALLIATIVE_CARE,
  SERVICE_PALLIATIVE_CERTIFICATE,
  SERVICE_PSYCHIATRIC_CARE,
  SERVICE_SPECIALISED_INTENSIVE_NURSE,
  SERVICE_TRACHEOSTOMY,
  SOLUTION_SHORT_TERM_STATIC_CARE,
} from "core/consts";
import { isCareSearch, isRehabSearch } from "core/model/auctions";
import { getAge } from "core/model/patients";
import { formatUnixDate } from "core/model/utils/dates";
import { formatDistance } from "core/model/utils/location";
import { formatMedicalSuppliesLocation } from "core/model/utils/location/formatters";
import { capitaliseFirst } from "core/model/utils/strings";
import {
  Auction,
  AuctionRequest,
  GetOntologyType,
  Solutions,
} from "core/types";
import { isValid } from "date-fns";
import { getOrderedItems } from "ds_legacy/components/ChipGroup";
import { solutionSortOrder } from "dsl/ecosystems/PatientCreatePage/PatientCreateForm";
import {
  ArrayChip,
  BooleanChip,
  NumberChip,
  OntologyChip,
  StringChip,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import { Locale as LocaleString } from "translations";
import Translations from "translations/types";

const shouldDisplayDuration = (duration: number, solutions: Solutions) =>
  solutions.includes(SOLUTION_SHORT_TERM_STATIC_CARE) ? duration <= 60 : true;

const getBirthDateRange = (birthDate: string, ageRange: string) =>
  `${birthDate}${
    ageRange && typeof ageRange === "string" ? ` (${ageRange})` : ""
  }`;

function computeCarestartFlexibility(
  auction: Auction,
  locale: LocaleString,
  translations: Translations,
): string | null {
  const careStartDate = auction.start_date;
  const flexible = auction.start_date_flexible
    ? ` ${translations.patient.flexible}`
    : "";

  if (!careStartDate) return null;
  return formatUnixDate(careStartDate, locale).concat(flexible);
}

function formatDistanceForZipCodeMessage(
  distance: number | null | undefined,
  translations: Translations,
): string {
  if (!distance || distance <= 0) {
    return "";
  }
  return distance > 0
    ? translations.patient.distanceFromYou({
        distance: formatDistance(distance),
      })
    : "";
}

export function StaticPatientProfileTransferChips({
  auction,
  auctionRequest,
  disabled = false,
  forClinic,
  getOntology,
  locale,
  showServices,
  showSolutions,
  showSpecializations,
  translations,
}: {
  disabled?: boolean;
  forClinic?: boolean;
  getOntology: GetOntologyType;
  locale: LocaleString;
  translations: Translations;
  auction: Auction;
  auctionRequest: AuctionRequest;
  showServices?: boolean;
  showSolutions?: boolean;
  showSpecializations?: boolean;
}) {
  const solutionsToUse = showSolutions
    ? auctionRequest?.solutions ?? auction.solutions ?? []
    : [];

  let zipcodeString = formatMedicalSuppliesLocation(auction);

  const distanceForZipCode = formatDistanceForZipCodeMessage(
    auctionRequest?.distance,
    translations,
  );

  zipcodeString = zipcodeString
    ? `${zipcodeString}${distanceForZipCode ? ` (${distanceForZipCode})` : ""}`
    : undefined;

  const services = showServices ? auction.services : [];

  const specializations = showSpecializations ? auction.specializations : [];

  const age =
    getAge(auction.patient.profile?.age_interval, auction.search_type) ?? "";

  return (
    <>
      <OntologyChip
        bold
        disabled={disabled}
        getOntology={getOntology}
        keyPrefix="patientType"
        selected
        type="patientType"
        value={forClinic && auction.search_type}
      />

      <ArrayChip
        bold
        disabled={disabled}
        getOntology={getOntology}
        keyPrefix="solution"
        primary
        type="solution"
        value={
          solutionsToUse.length > 0
            ? getOrderedItems(
                solutionSortOrder,
                solutionsToUse.map((value) => ({ value })),
              )?.map((item) => item.value) || null
            : null
        }
      />
      <ArrayChip
        bold
        disabled={disabled}
        getOntology={getOntology}
        keyPrefix="specializations"
        primary
        type="specializations"
        value={specializations}
      />
      {auction.profile?.is_elective && (
        <StringChip
          bold
          keyPrefix="elective"
          selected
          textBreak
          value={translations.careproviderApp.showElectivePatient}
        />
      )}
      <StringChip
        bold
        disabled={disabled}
        keyPrefix="start_date"
        prefix={translations.patient.expectedStartDate}
        primary
        value={computeCarestartFlexibility(auction, locale, translations)}
      />
      {isRehabSearch(auction?.search_type) &&
        auction?.patient?.profile?.discharge_date &&
        isValid(auction.patient.profile.discharge_date) && (
          <StringChip
            bold
            disabled={disabled}
            keyPrefix="discharge_date"
            prefix={translations.patient.expectedDischargeDate}
            primary
            value={formatUnixDate(
              auction.patient.profile.discharge_date,
              locale,
            )}
          />
        )}
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="direct_transfer_necessary"
        label={translations.patient.directTransfer}
        primary
        value={!!(auction.patient.profile?.direct_transfer_necessary != null)}
      />
      {isCareSearch(auction?.search_type) && (
        <>
          <NumberChip
            bold
            disabled={disabled}
            keyPrefix="care_duration_in_days"
            prefix={translations.patient.careDuration}
            primary
            transform={(value) =>
              getOntology({ key: value, type: "careDurations" })
            }
            value={
              ((forClinic ||
                shouldDisplayDuration(
                  auction.patient.profile?.care_duration_in_days ?? 0,
                  solutionsToUse,
                )) &&
                auction.patient.profile?.care_duration_in_days) ||
              null
            }
          />
          <BooleanChip
            bold
            disabled={disabled}
            keyPrefix="long_term_stay"
            label={capitaliseFirst(
              translations.patient.interestedStayLongTermRequest,
            )}
            primary
            value={!!auction.patient.profile?.interested_long_term_stay}
          />
        </>
      )}
      {auction?.patient?.profile?.birth_date?.decrypted ? (
        <StringChip
          bold
          disabled={disabled}
          keyPrefix="birth_date"
          prefix={translations.patient.dateOfBirth}
          primary
          value={getBirthDateRange(
            auction.patient?.profile?.birth_date?.decrypted ?? "",
            age,
          )}
        />
      ) : (
        <StringChip
          bold
          disabled={disabled}
          keyPrefix="age"
          prefix={translations.patient.age}
          primary
          value={age}
        />
      )}
      <StringChip
        bold
        disabled={disabled}
        keyPrefix="zipcode"
        prefix={translations.address.zipcode}
        primary
        value={zipcodeString}
      />
      <StringChip
        bold
        disabled={disabled}
        keyPrefix="radius_in_km"
        prefix={translations.patient.searchRadius}
        primary
        suffix="km"
        value={
          forClinic && auction.radius_in_meter
            ? (auction.radius_in_meter / 1000).toString()
            : null
        }
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="closed_area"
        label={translations.patient.closedAreaNeeded}
        primary
        value={!!services?.includes(SERVICE_CLOSED_AREA)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="closed_area"
        label={translations.patient.mentalState.protectedAreaNeeded.label}
        primary
        value={!!services?.includes(SERVICE_CARE_PROTECTED_AREA)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="palliative_care"
        label={translations.patient.palliativeCare.label}
        primary
        value={!!services?.includes(SERVICE_PALLIATIVE_CARE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="palliative_certificate"
        label={
          translations.patient.medicalDiagnosis.palliativeCertificateNeeded
        }
        primary
        value={!!services?.includes(SERVICE_PALLIATIVE_CERTIFICATE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="tracheostomy"
        label={translations.patient.careNeeds.tracheostomy.label}
        primary
        value={!!services?.includes(SERVICE_TRACHEOSTOMY)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="specialised_intensive_nurse"
        label={translations.ontologies.service.values.specialisedIntensiveNurse}
        primary
        value={!!services?.includes(SERVICE_SPECIALISED_INTENSIVE_NURSE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="psychiatric_care"
        label={translations.patient.careNeeds.psychiatric_care.label}
        primary
        value={!!services?.includes(SERVICE_PSYCHIATRIC_CARE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="basic_medical_care"
        label={translations.ontologies.service.values.basicMedicalCare}
        primary
        value={!!services?.includes(SERVICE_BASIC_MEDICAL_CARE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="basic_care"
        label={translations.patient.careNeeds.baseCare}
        primary
        value={!!services?.includes(SERVICE_BASIC_CARE)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="companionship"
        label={translations.patient.careNeeds.companionship}
        primary
        value={!!services?.includes(SERVICE_COMPANIONSHIP)}
      />
      <BooleanChip
        bold
        disabled={disabled}
        keyPrefix="dementia"
        label={translations.patient.dementia}
        primary
        value={!!services?.includes(SERVICE_DEMENTIA)}
      />
    </>
  );
}
